import React from 'react';
import {
  CheckCircle as ExcellentIcon,
  RemoveCircle as FairIcon,
  ThumbUp as GoodIcon,
  Warning as PoorIcon,
} from '@mui/icons-material';
import { Box, CircularProgress,Typography } from '@mui/material';

interface QualityScoreDisplayProps {
  score: number; // 0-5 scale
  showLabel?: boolean;
  size?: number;
}

export const QualityScoreDisplay: React.FC<QualityScoreDisplayProps> = ({
  score,
  showLabel = true,
  size = 80,
}) => {
  const percentage = (score / 5) * 100;

  // Color and icon based on score
  const getScoreConfig = () => {
    if (score >= 4) {
      return {
        color: '#4caf50',
        label: 'Excellent',
        icon: <ExcellentIcon sx={{ fontSize: size * 0.4 }} />,
      };
    }
    if (score >= 3) {
      return {
        color: '#8bc34a',
        label: 'Good',
        icon: <GoodIcon sx={{ fontSize: size * 0.4 }} />,
      };
    }
    if (score >= 2) {
      return {
        color: '#ff9800',
        label: 'Fair',
        icon: <FairIcon sx={{ fontSize: size * 0.4 }} />,
      };
    }
    return {
      color: '#f44336',
      label: 'Needs Work',
      icon: <PoorIcon sx={{ fontSize: size * 0.4 }} />,
    };
  };

  const config = getScoreConfig();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={size}
          thickness={4}
          sx={{
            color: 'rgba(0, 0, 0, 0.1)',
            position: 'absolute',
          }}
        />
        <CircularProgress
          variant="determinate"
          value={percentage}
          size={size}
          thickness={4}
          sx={{
            color: config.color,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: 700, fontSize: size * 0.25, color: config.color }}
          >
            {score.toFixed(1)}
          </Typography>
          <Typography variant="caption" sx={{ fontSize: size * 0.1, color: 'text.secondary' }}>
            / 5
          </Typography>
        </Box>
      </Box>
      {showLabel && (
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
            {React.cloneElement(config.icon as React.ReactElement<Record<string, unknown>>, { sx: { color: config.color } })}
            <Typography variant="body2" sx={{ fontWeight: 600, color: config.color }}>
              {config.label}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};
